import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { DateField } from '@/components/molecules/date-field'
import { Cascader } from 'antd'
import moment from 'moment'
import { getReservationsBoundaryDates } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'

type Prop = {
  value: string | undefined
  onChange: (val: string | undefined) => void
}
type CascaderOptionsType = { value: any; label: string; children: { value: any; label: string }[] }

export const SalesMonthCascader: React.FC<Prop> = prop => {
  const { t, i18n } = useTranslation()
  const { errorHandler } = useErrorHandler()

  const [isMonthCascaderOpen, setIsMonthCascaderOpen] = useState<boolean>(false)
  const [monthOptions, setMonthOptions] = useState<CascaderOptionsType[]>([])

  const buildMonthOptions = (reservationsBoundaryDates: { min: string; max: string }) => {
    if (typeof reservationsBoundaryDates?.min !== 'string' || typeof reservationsBoundaryDates?.max !== 'string') return

    const min = moment(reservationsBoundaryDates.min)
    const max = moment(reservationsBoundaryDates.max)
    const minYear = min.get('year')
    const minMonth = min.get('month')
    const maxYear = max.get('year')
    const maxMonth = max.get('month')

    const options: CascaderOptionsType[] = []
    for (let year = minYear; year <= maxYear; year++) {
      let months: number[] = []
      if (year === minYear) {
        months = [...Array(12 - minMonth)].map((item, index) => index + minMonth)
      } else if (year === maxYear) {
        months = [...Array(maxMonth + 1)].map((item, index) => index)
      } else {
        months = [...Array(12)].map((item, index) => index)
      }
      options.push({
        value: String(year),
        label: String(year + t('+year')),
        children: months.map(value => ({
          value: String(value),
          label: dayjs().month(value).locale(i18n.language).format('MMM'),
        })),
      })
    }

    setMonthOptions(options)
  }

  const fetchReservationsBoundaryDates = async () => {
    try {
      const reservationsBoundaryDates = await getReservationsBoundaryDates()
      buildMonthOptions(reservationsBoundaryDates)
    } catch (error) {
      errorHandler(error)
    }
  }

  useEffect(() => {
    fetchReservationsBoundaryDates()
  }, [])

  return (
    <div css={{ position: 'relative' }} className={isMonthCascaderOpen ? 'date-change-container--cascader-active' : ''}>
      <DateField value={prop.value} onChange={v => prop.onChange(v?.format('YYYY-MM-DD'))} displayFormat={t('YYYY-MM')} />
      <Cascader
        defaultValue={prop.value ? [String(dayjs(prop.value).year()), String(dayjs(prop.value).month())] : []}
        options={monthOptions}
        onChange={yearAndMonth =>
          prop.onChange(dayjs().startOf('month').year(Number(yearAndMonth[0])).month(Number(yearAndMonth[1])).format('YYYY-MM-DD'))
        }
        allowClear={false}
        css={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, opacity: 0 }}
        popupClassName="popup-cascader-customize-date"
        onPopupVisibleChange={value => setIsMonthCascaderOpen(value)}
      />
    </div>
  )
}
